<script setup lang="ts">
  // Dumb chip for the table footer: renders the table-discount state and
  // delegates the flow to use-table-discount via the `open` event.
  import { computed } from 'vue';
  import { ITableDiscountState } from '@/domain/pos/table-discount-derivation';
  import { usePosMoney } from '@/composition/use-pos-money';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';

  const props = defineProps<{
    state: ITableDiscountState;
    saving: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'open'): void;
  }>();

  const money = usePosMoney();

  const activeLabel = computed(() => {
    const active = props.state.active;
    if (!active) return '';
    return active.isPercentage
      ? `-${active.value}%`
      : `-${money.formatComputedMoney(active.value)}`;
  });
</script>

<template>
  <div class="pos-v2-table-discount-chip">
    <!-- POS-owned tables still open the modal: it explains the block in a
         visible note (a disabled button with a title tooltip says nothing on
         the touch terminals this UI targets) and keeps Apply disabled. -->
    <PosUiButton
      v-if="!state.active"
      color="neutral"
      variant="ghost"
      size="md"
      icon="tag-outline"
      class="chip-button"
      :disabled="saving"
      @click="emit('open')"
    >
      {{ $t('posV2.tableDiscount') }}
    </PosUiButton>

    <PosUiButton
      v-else
      color="info"
      variant="soft"
      size="md"
      icon="tag-outline"
      class="chip-button"
      :disabled="saving"
      @click="emit('open')"
    >
      {{ activeLabel }}
      <span v-if="state.uncoveredEligibleCount > 0" class="uncovered-hint">
        <span class="uncovered-dot" />
        {{ state.uncoveredEligibleCount }}
      </span>
    </PosUiButton>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-table-discount-chip {
    @apply tw-inline-flex;

    .chip-button {
      @apply tw-whitespace-nowrap;
    }

    .uncovered-hint {
      @apply tw-ml-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-amber-600;

      .uncovered-dot {
        @apply tw-h-2 tw-w-2 tw-rounded-full tw-bg-amber-500;
      }
    }
  }
</style>
